import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { orderAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Package,
  Clock,
  CheckCircle2,
  XCircle,
  ChefHat,
  Phone,
  MapPin
} from 'lucide-react';
import AdminLayout from '../../components/layouts/AdminLayout';

const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: Clock, className: 'status-pending' },
  confirmed: { label: 'Confirmed', icon: CheckCircle2, className: 'status-confirmed' },
  preparing: { label: 'Preparing', icon: ChefHat, className: 'status-preparing' },
  ready: { label: 'Ready', icon: Package, className: 'status-ready' },
  delivered: { label: 'Delivered', icon: CheckCircle2, className: 'status-delivered' },
  cancelled: { label: 'Cancelled', icon: XCircle, className: 'status-cancelled' },
};

const STATUS_OPTIONS = ['pending', 'confirmed', 'preparing', 'ready', 'delivered', 'cancelled'];

export default function AdminOrders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const response = await orderAPI.getAll(user.id, 'admin');
      setOrders(response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await orderAPI.updateStatus(orderId, newStatus, user.id);
      setOrders(orders.map(o => 
        o.id === orderId ? { ...o, status: newStatus } : o
      ));
      toast.success(`Order status updated to ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const filteredOrders = filter === 'all' 
    ? orders 
    : orders.filter(o => o.status === filter);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold">All Orders</h1>
            <p className="text-muted-foreground">{orders.length} total orders</p>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Orders</SelectItem>
              {STATUS_OPTIONS.map((status) => (
                <SelectItem key={status} value={status}>
                  {STATUS_CONFIG[status].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Orders List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <Skeleton className="h-4 w-1/3 mb-2" />
                  <Skeleton className="h-3 w-1/2 mb-4" />
                  <Skeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="empty-state py-20">
            <Package className="empty-state-icon" />
            <h3 className="font-heading text-xl font-semibold mb-2">No orders found</h3>
            <p className="text-muted-foreground">
              {filter === 'all' ? 'Orders will appear here' : `No ${filter} orders`}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map((order) => {
              const statusConfig = STATUS_CONFIG[order.status];
              const StatusIcon = statusConfig.icon;
              
              return (
                <Card key={order.id} data-testid={`order-${order.id}`}>
                  <CardContent className="p-4">
                    {/* Order Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-mono text-sm text-muted-foreground">
                            #{order.id.slice(0, 8).toUpperCase()}
                          </p>
                          <Badge className={`${statusConfig.className} border`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(order.created_at), 'dd MMM yyyy, hh:mm a')}
                        </p>
                      </div>
                      <Select 
                        value={order.status} 
                        onValueChange={(value) => handleStatusChange(order.id, value)}
                      >
                        <SelectTrigger className="w-[130px]" data-testid={`status-select-${order.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((status) => (
                            <SelectItem key={status} value={status}>
                              {STATUS_CONFIG[status].label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Customer Info */}
                    <div className="bg-muted/50 rounded-xl p-3 mb-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-primary font-semibold text-sm">
                            {order.buyer_name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold text-sm">{order.buyer_name}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {order.buyer_mobile}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground flex items-start gap-1">
                        <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        {order.delivery_address}
                      </p>
                    </div>

                    {/* Order Items */}
                    <div className="space-y-2 mb-3">
                      <p className="text-sm font-medium">Order Items:</p>
                      {order.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-1 border-b border-border last:border-0">
                          <span>{item.quantity}x {item.item_name}</span>
                          <span className="font-medium">
                            {item.price === 0 ? 'Free' : `₹${item.price * item.quantity}`}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Order Total */}
                    <div className="flex items-center justify-between pt-2 border-t border-border">
                      <span className="text-sm text-muted-foreground">
                        {order.items.reduce((sum, item) => sum + item.quantity, 0)} items
                      </span>
                      <span className="font-semibold text-primary">
                        Total: ₹{order.total_amount.toFixed(2)}
                      </span>
                    </div>

                    {order.notes && (
                      <p className="text-xs text-muted-foreground mt-2 p-2 bg-muted/30 rounded">
                        Note: {order.notes}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
